import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSite } from "@/hooks/useSite";
import { getCookieConsent } from "@/lib/consent";
import { track, trackClicks } from "@/lib/track";

const once = new Set();

const addScript = (id, attrs = {}, code = "") => {
  if (once.has(id)) return;
  once.add(id);
  const s = document.createElement("script");
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  if (code) s.text = code;
  document.head.appendChild(s);
};

const injectHtml = (id, html, target) => {
  if (!html || once.has(id)) return;
  once.add(id);
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  tpl.content.childNodes.forEach((n) => {
    if (n.nodeName === "SCRIPT") {
      const s = document.createElement("script");
      [...n.attributes].forEach((a) => s.setAttribute(a.name, a.value));
      s.text = n.textContent;
      target.appendChild(s);
    } else target.appendChild(n.cloneNode(true));
  });
};

const useConsent = () => {
  const [c, setC] = useState(getCookieConsent);
  useEffect(() => {
    const on = () => setC(getCookieConsent());
    window.addEventListener("pp:consent", on);
    return () => window.removeEventListener("pp:consent", on);
  }, []);
  return c || {};
};

export const Tracking = () => {
  const t = useSite().data?.settings?.tracking;
  const consent = useConsent();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!t) return;
    if (t.gsc_verification && !document.querySelector('meta[name="google-site-verification"]')) {
      const m = document.createElement("meta");
      m.name = "google-site-verification";
      m.content = t.gsc_verification;
      document.head.appendChild(m);
    }
    if (consent.analytics) {
      if (t.ga4_id) {
        addScript("ga4-src", { async: "", src: `https://www.googletagmanager.com/gtag/js?id=${t.ga4_id}` });
        addScript("ga4-init", {}, `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${t.ga4_id}');`);
      }
      if (t.gtm_id) addScript("gtm", {}, `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${t.gtm_id}');`);
      injectHtml("custom-head", t.head_script, document.head);
      injectHtml("custom-body", t.body_script, document.body);
    }
    if (consent.marketing && t.meta_pixel_id) {
      addScript("fbq", {}, `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${t.meta_pixel_id}');fbq('track','PageView');`);
    }
  }, [t, consent.analytics, consent.marketing]);

  const links = useSite().data?.settings?.links;
  useEffect(() => {
    const h = trackClicks(links || {});
    document.addEventListener("click", h, true);
    return () => document.removeEventListener("click", h, true);
  }, [links]);

  useEffect(() => {
    track("page_view");
    if (window.gtag && t?.ga4_id) window.gtag("event", "page_view", { page_path: pathname });
    if (window.fbq && t?.meta_pixel_id) window.fbq("track", "PageView");
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};
